import { IconButton, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography } from '@material-ui/core';
import {AddCircle, Edit, Delete, VisibilityOff, Visibility, Settings, Group} from '@material-ui/icons';
import React, {useEffect, useRef, useState} from 'react';
import { useHistory } from 'react-router-dom';
import Cube from '../../../Domain/Models/Cube';
import { cubeRepository } from '../../../Domain/Repositories/CubeRepository';
import CubeModal from './CubeModal';
import DecisionModal from "../../UiComponents/DecisionModal";
import ErrorCard from "../../UiComponents/ErrorCard";
import ErrorCardController from "../../UiComponents/ErrorCardController";
import Loading from "../../UiComponents/Loading";
import AddTestGroupModal from "./AddTestGroupModal";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  title: {
    flexGrow: 1
  },
  clickableCell: {
    cursor: 'pointer',
  },
  maintenanceModeCube: {
    backgroundColor: '#ff2929'
  }
}));

export default function CubeList()
{
  const classes = useStyles();
  const history = useHistory();

  const errorCardControllerRef = useRef<ErrorCardController>(null);

  const [idVisible, setIdVisible] = useState(false);
  const [cubeDialogOpen, setCubeDialogOpen] = useState(false);
  const [cubeToEdit, setCubeToEdit] = useState<Cube>();
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [maintenanceDecisionDialogOpen, setMaintenanceDecisionDialogOpen] = useState(false);
  const [testGroupDialogOpen, setTestGroupDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAddClicked = () =>
  {
    setCubeDialogOpen(true);
  };

  const handleCubeClicked = (cube: Cube) =>
  {
    history.push(`/cube/${cube.id}`);
  };

  const handleEditCubeClicked = (cube: Cube) =>
  {
    setCubeToEdit(cube);
    setCubeDialogOpen(true);
  }

  const handleMaintenanceModeClicked = (cube: Cube) =>
  {
    setCubeToEdit(cube);
    setMaintenanceDecisionDialogOpen(true);
  }

  const handleTestGroupClicked = (cube: Cube) =>
  {
    setCubeToEdit(cube);
    setTestGroupDialogOpen(true);
  }

  const handleDeleteCubeClicked = (cube: Cube) =>
  {
    setCubeToEdit(cube);
    setDecisionDialogOpen(true);
  }

  const handleDeleteDecisionAccepted = () =>
  {
    handleDialogClose();
    setLoading(true);

    if (cubeToEdit === undefined)
    {
      return;
    }

    cubeRepository.delete(cubeToEdit)
        .then(() => setLoading(false))
        .catch((e: Error) =>
        {
          setLoading(false);
          errorCardControllerRef.current?.addErrorMessage(e.message)
        });
  }

  const handleMaintenanceModeAccepted = () =>
  {
    handleDialogClose();
    setLoading(true);

    if (cubeToEdit === undefined)
    {
      return;
    }

    cubeRepository.maintenance(cubeToEdit, !cubeToEdit.maintenanceMode)
      .then(() => forceReload())
      .then(() => setLoading(false))
      .catch((e: Error) =>
      {
        setLoading(false);
        errorCardControllerRef.current?.addErrorMessage(e.message)
      });
  }

  const handleDialogClose = () =>
  {
    setCubeDialogOpen(false);
    setDecisionDialogOpen(false);
    setMaintenanceDecisionDialogOpen(false);
    setTestGroupDialogOpen(false);
    setCubeToEdit(undefined);
  };
  
  const forceReload = () =>
  {
    setLoading(true);
    cubeRepository.load()
      .then(() => setLoading(false))
      .catch((e: Error) =>
      {
        setLoading(false);
        errorCardControllerRef.current?.addErrorMessage(e.message);
      })
  }
  
  useEffect(() =>
  {
    forceReload();
  }, [])

  return (
      <>
        <CubeModal
            open={cubeDialogOpen}
            cube={cubeToEdit}
            onClose={handleDialogClose}
        />
        <DecisionModal
            open={decisionDialogOpen}
            message={`Are you sure you want to delete ${cubeToEdit?.name}?`}
            onAccept={handleDeleteDecisionAccepted}
            onCancel={handleDialogClose}
        />
        <DecisionModal
          open={maintenanceDecisionDialogOpen}
          message={`Are you sure you want to ${cubeToEdit?.maintenanceMode ? 'DISABLE' : 'ENABLE'} maintenance mode for ${cubeToEdit?.name}?`}
          onAccept={handleMaintenanceModeAccepted}
          onCancel={handleDialogClose}
        />
        {cubeToEdit !== undefined && (
          <AddTestGroupModal
            open={testGroupDialogOpen}
            onClose={handleDialogClose}
            cube={cubeToEdit}
          />
        )}

        <Loading isLoading={loading}>
          <ErrorCardController ref={errorCardControllerRef}/>

          <Paper className={classes.paper}>
            <Toolbar>
              <Typography className={classes.title} component="h2" variant="h6" color="primary" gutterBottom>
                Cubes
              </Typography>
              <IconButton size="small" color="default" onClick={() => setIdVisible(!idVisible)}>
                { idVisible ? <VisibilityOff/> : <Visibility/> }
              </IconButton>
              <IconButton size="medium" color="default" onClick={handleAddClicked}>
                <AddCircle/>
              </IconButton>
            </Toolbar>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {idVisible && <TableCell>ID</TableCell>}
                    <TableCell>Name</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Game Type</TableCell>
                    <TableCell>Num Players</TableCell>
                    <TableCell>Play Time</TableCell>
                    <TableCell>Bullet Time</TableCell>
                    <TableCell/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cubeRepository.cubes.map((row) => (
                      <TableRow
                        key={row.id}
                        className={row.maintenanceMode ? classes.maintenanceModeCube : undefined}
                        hover
                      >
                        {idVisible && <TableCell scope="row">{row.id}</TableCell>}
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleCubeClicked(row)}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleCubeClicked(row)}
                        >
                          {row.journeyPhases.toString()}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleCubeClicked(row)}
                        >
                          {row.gameType}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleCubeClicked(row)}
                        >
                          {row.numberOfPlayers.toString()}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleCubeClicked(row)}
                        >
                          {row.averagePlayTimeSeconds.toString()}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleCubeClicked(row)}
                        >
                          {row.isBulletTime ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="default" onClick={() => handleTestGroupClicked(row)}>
                            <Group/>
                          </IconButton>
                          <IconButton size="small" color="default" onClick={() => handleMaintenanceModeClicked(row)}>
                            <Settings/>
                          </IconButton>
                          <IconButton size="small" color="default" onClick={() => handleEditCubeClicked(row)}>
                            <Edit/>
                          </IconButton>
                          <IconButton size="small" color="default" onClick={() => handleDeleteCubeClicked(row)}>
                            <Delete/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          </Paper>
        </Loading>
      </>
  );
}